import configService from './config';
import guveryFetchApi from './guveryFetchApi';
import * as METHODS from './methods';
import type {
  ConfirmShippingPayload,
  DocumentTypeValue,
  PreconfirmPayload,
} from '#root/models/Shipping';
import type { AddressDocumentPayload } from '#root/models/Address';

const guveryApi = configService.apiGuvery;

/**
 * Confirm shipping with address delivery (envío a domicilio)
 * Optionally include valorem (valorization) if enabled, and the receiver's document
 * when the selected address doesn't have one yet
 */
const confirmAddress = async (
  shippingId: number,
  addressId: number,
  valorem?: number,
  document?: AddressDocumentPayload
) => {
  const payload: ConfirmShippingPayload = {
    shipping_id: shippingId,
    delivery_address_id: addressId,
  };

  if (valorem !== undefined && valorem !== null) {
    payload.valorem = valorem;
  }

  if (document) {
    payload.document_type = document.document_type;
    payload.document_number = document.document_number;
  }

  const { response } = await guveryFetchApi({
    url: `${guveryApi}/user/shipping/confirmed`,
    options: METHODS.POST(payload),
    message: {
      success: 'Envío confirmado correctamente',
      error: 'No se pudo confirmar el envío.',
    },
  });

  return { ...response };
};

/**
 * Confirm shipping with pickup (recojo en persona)
 * Changes status to POR_RECOGER
 */
const confirmPickup = async (
  shippingId: number,
  fullName: string,
  docType: DocumentTypeValue,
  docNumber: string
) => {
  const payload: ConfirmShippingPayload = {
    shipping_id: shippingId,
    is_pickup: true,
    pickup_full_name: fullName,
    pickup_document_type: docType,
    pickup_document_number: docNumber,
  };

  const { response } = await guveryFetchApi({
    url: `${guveryApi}/user/shipping/confirmed`,
    options: METHODS.POST(payload),
    message: {
      success: 'Recojo confirmado correctamente',
      error: 'No se pudo confirmar el recojo.',
    },
  });

  return { ...response };
};

/**
 * Configuración del envío: contexto para el aviso de envío conjunto, tarifas del seguro y
 * (sin uso en la UI) los acuerdos configurables. data: PreconfirmSummary
 */
const getPreconfirm = async () => {
  const { response } = await guveryFetchApi({
    url: `${guveryApi}/user/shipping/preconfirm`,
    options: METHODS.GET(),
    message: {
      success: 'Configuración del envío obtenida.',
      error: 'No se pudo cargar la configuración del envío.',
    },
  });

  return { ...response };
};

/**
 * Guarda (upsert) la dirección y el seguro del envío de los acuerdos dados.
 * data: PreconfirmSummary actualizado
 */
const preconfirm = async (payload: PreconfirmPayload) => {
  const { response } = await guveryFetchApi({
    url: `${guveryApi}/user/shipping/preconfirm`,
    options: METHODS.POST(payload),
    message: {
      success: 'Guardamos la configuración de tu envío.',
      error: 'No se pudo guardar la configuración del envío.',
    },
  });

  return { ...response };
};

/**
 * Quita la configuración del envío de los acuerdos dados (con al menos uno).
 * data: PreconfirmSummary actualizado
 */
const cancelPreconfirm = async (dealIds: number[]) => {
  const { response } = await guveryFetchApi({
    url: `${guveryApi}/user/shipping/preconfirm/cancel`,
    options: METHODS.POST({ deal_ids: dealIds }),
    message: {
      success: 'Quitamos la configuración de tu envío.',
      error: 'No se pudo quitar la configuración del envío.',
    },
  });

  return { ...response };
};

const shippingService = {
  confirmAddress,
  confirmPickup,
  getPreconfirm,
  preconfirm,
  cancelPreconfirm,
};

export default shippingService;
